import {
  Box,
  Typography,
  TextareaAutosize,
  Button,
  CircularProgress,
  useTheme,
  useMediaQuery,
  Card,
  CardContent,
  Skeleton,
  Divider,
  Paper,
} from "@mui/material";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import { tokens } from "../../../../../theme";
import type { RootState } from "../../../../../store/Index";
import apiService from "../../../../../service/Api";
import { useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { useSelector } from "react-redux";
import { useCallback, useEffect, useRef, useState } from "react";
import BookIcon from "@mui/icons-material/Book";
import { User } from "../../../../users-admin/types/User";
import { MemoryBook } from "../../../../../types/memory-table";
import { MemoryBookTableRow } from "./MemoryBookTableRow";
import { CurrentMemoryBookInfo } from "./CurrentMemoryBookInfo";
import { ReactComponent as CloudIcon } from "./../../../../../assets/cloud-icon.svg";
import { compressPdf } from "../../../../../utils/compressPdf";

const MAX_BYTES = 10 * 1024 * 1024;

export const MemoryTableUpload: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const theme = useTheme();
  const reduxTheme = useSelector((state: RootState) => state.theme);
  const colors = tokens(theme.palette.mode, reduxTheme);
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [selectedBook, setSelectedBook] = useState<File | null>(null);
  const [canvaLink, setCanvaLink] = useState<string>("");
  const [uploadNotes, setUploadNotes] = useState<string>("");
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<boolean>(false);
  const [isUploading, setIsUploading] = useState<boolean>(false);
  const [memoryBooks, setMemoryBooks] = useState<MemoryBook[]>([]);
  const [uploadUser, setUploadUser] = useState<User | null>(null);
  const [isCompressing, setIsCompressing] = useState(false);
  const [sizeInfo, setSizeInfo] = useState<{
    before: number;
    after: number;
  } | null>(null);

  const fetchMemoryBooks = useCallback(async () => {
    setIsLoading(true);
    setError(false);
    try {
      const response = await apiService.get<{ results: MemoryBook[] }>(
        `/api/streams/memory-book/?stream=${id}`,
      );
      setMemoryBooks(response.data.results || []);
    } catch (err) {
      console.error(err);
      toast.error("Failed to load memory books. Please try again.", {
        position: "top-right",
        autoClose: 5000,
        theme: "light",
      });
      setError(true);
    } finally {
      setIsLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchMemoryBooks();
  }, [fetchMemoryBooks]);

  const fetchUser = useCallback(async () => {
    try {
      const user = memoryBooks[memoryBooks.length - 1].created_by;
      const response = await apiService.get<User>(
        `/api/authentication/users/${user}/`,
      );
      setUploadUser(response.data);
    } catch (err) {
      console.error(err);
    }
  }, [memoryBooks]);

  useEffect(() => {
    if (memoryBooks.length > 0) {
      const memoryBook = memoryBooks[0];
      setCanvaLink(memoryBook.canva_memory_book_link || "");
      setUploadNotes(memoryBook.notes || "");
      fetchUser();
    }
  }, [fetchUser, memoryBooks]);

  const browseFile = () => fileInputRef.current?.click();

  const onUploadChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;
    const file = files[0];
    e.target.value = "";
    setIsCompressing(true);
    setSizeInfo(null);
    setSelectedBook(null);
    const toastId = toast.info("Compressing PDF...", {
      position: "top-right",
      autoClose: false,
    });
    try {
      const compressed = await compressPdf(file);
      toast.dismiss(toastId);
      setSizeInfo({ before: file.size, after: compressed.size });
      if (compressed.size > MAX_BYTES) {
        toast.warn(
          `Still ${(compressed.size / 1024 / 1024).toFixed(1)}MB after compression. ` +
            `Re-export from Canva using "Standard" (not "Print") quality.`,
          { position: "top-right", autoClose: 12000 },
        );
      } else {
        toast.success("File compressed successfully", {
          position: "top-right",
          autoClose: 5000,
        });
        setSelectedBook(compressed);
      }
    } catch (err) {
      toast.dismiss(toastId);
      toast.error("Compression failed. Try a different file.", {
        position: "top-right",
        autoClose: 5000,
      });
      console.error(err);
    } finally {
      setIsCompressing(false);
    }
  };

  const updateCurrentBook = async () => {
    setIsLoading(true);
    try {
      const currentBook: MemoryBook = memoryBooks[0];
      const formData = new FormData();
      formData.append("canva_memory_book_link", canvaLink);
      formData.append("notes", uploadNotes);
      await apiService.patch(
        `/api/streams/memory-book/${currentBook.id}/`,
        formData,
      );
      toast.success("Details updated successfully.", {
        position: "top-right",
        autoClose: 5000,
        theme: "light",
      });
    } catch (error) {
      console.error(error);
      toast.error("Failed to update details. Please try again.", {
        position: "top-right",
        autoClose: 5000,
        theme: "light",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const deleteMemoryBook = async (memoryBookId: number) => {
    try {
      setIsLoading(true);
      await apiService.delete(`/api/streams/memory-book/${memoryBookId}/`);
      toast.success("Memory book deleted successfully.", {
        position: "top-right",
        autoClose: 5000,
        theme: "light",
      });
      await fetchMemoryBooks();
    } catch (error) {
      console.error(error);
      toast.error("Failed to delete memory book. Please try again.", {
        position: "top-right",
        autoClose: 5000,
        theme: "light",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const uploadBook = async () => {
    if (selectedBook === null) {
      toast.warn("You have not selected any book", {
        position: "top-right",
        autoClose: 5000,
        theme: "light",
      });
      return;
    }
    setIsUploading(true);
    const formData = new FormData();
    formData.append("file", selectedBook);
    formData.append("stream_id", `${id}`);
    formData.append("canva_memory_book_link", canvaLink);
    formData.append("notes", uploadNotes || "No notes");
    try {
      await apiService.post(`/api/streams/memory-book/`, formData);
      toast.success(`${selectedBook.name} has been uploaded`, {
        position: "top-right",
        autoClose: 5000,
        theme: "light",
      });
      fetchMemoryBooks();
      setSelectedBook(null);
    } catch (error) {
      console.error(error);
      toast.error("Failed to upload book. Please try again", {
        position: "top-right",
        autoClose: 5000,
        theme: "light",
      });
    } finally {
      setIsUploading(false);
    }
  };

  const uploadedBooks = memoryBooks.filter((book) => !!book.file);

  const dropZone = (
    <Box
      sx={{
        width: "100%",
        minHeight: { xs: 180, sm: 310 },
        borderRadius: 2,
        border: "1px solid #CBD5E1",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        flexDirection: "column",
        gap: 1,
        p: 2,
      }}
    >
      {isCompressing ? (
        <>
          <CircularProgress size={36} sx={{ mb: 1 }} />
          <Typography variant="body2" textAlign="center" color="text.secondary">
            Compressing PDF...
          </Typography>
        </>
      ) : selectedBook === null ? (
        <>
          <CloudIcon style={{ height: 50, width: 50 }} />
          <Typography variant="body2" textAlign="center" mb={1}>
            Drag and drop your PDF here (max 10MB)
            <br /> or
          </Typography>
        </>
      ) : (
        <>
          <BookIcon sx={{ height: 44, width: 44, color: "#CBD5E1" }} />
          <Typography variant="body2" textAlign="center" mb={0.5}>
            {selectedBook.name}
          </Typography>
          {sizeInfo !== null && (
            <Typography variant="caption" color={colors.primary[700]}>
              {(sizeInfo.after / 1024 / 1024).toFixed(1)} MB
            </Typography>
          )}
        </>
      )}
      <input
        onChange={onUploadChange}
        accept=".pdf"
        ref={fileInputRef}
        type="file"
        style={{ display: "none" }}
        disabled={isCompressing}
      />
      <Button onClick={browseFile} variant="contained" disabled={isCompressing}>
        Browse file
      </Button>
    </Box>
  );

  const notesPanel = (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      <Box>
        <Typography variant="body1" fontWeight={600} mb={1}>
          Canva Link
        </Typography>
        <TextareaAutosize
          value={canvaLink}
          onChange={(e) => setCanvaLink(e.target.value)}
          placeholder="Paste canva link here"
          style={{
            width: "100%",
            resize: "none",
            padding: "0.5rem",
            minHeight: 50,
            borderRadius: 4,
            backgroundColor: colors.primary[800],
            border: "none",
            boxSizing: "border-box",
          }}
        />
      </Box>

      <Box>
        <Typography variant="h5" fontWeight={600} mb={1}>
          Notes
        </Typography>
        <Box sx={{ position: "relative" }}>
          <TextareaAutosize
            value={uploadNotes}
            onChange={(e) => setUploadNotes(e.target.value)}
            placeholder="Add any special instructions or notes"
            style={{
              width: "100%",
              resize: "none",
              padding: "0.5rem",
              paddingBottom: "3rem",
              minHeight: isMobile ? 120 : 149,
              borderRadius: 4,
              backgroundColor: colors.primary[800],
              border: "none",
              boxSizing: "border-box",
            }}
          />
          <Button
            variant="contained"
            disabled={isUploading}
            onClick={selectedBook ? uploadBook : updateCurrentBook}
            sx={{ position: "absolute", right: "0.7em", bottom: "0.8em" }}
          >
            {isUploading ? (
              <CircularProgress size={20} />
            ) : selectedBook ? (
              "Upload"
            ) : (
              "Update"
            )}
          </Button>
        </Box>
      </Box>
    </Box>
  );

  const historyMobile = (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
      {isLoading &&
        [...Array(3)].map((_, i) => (
          <Card key={i} variant="outlined" sx={{ borderRadius: 2 }}>
            <CardContent sx={{ p: 1.5, "&:last-child": { pb: 1.5 } }}>
              <Skeleton variant="text" width="60%" height={20} />
              <Skeleton variant="text" width="40%" height={16} />
              <Skeleton variant="text" width="30%" height={16} />
            </CardContent>
          </Card>
        ))}

      {error && !isLoading && (
        <Box textAlign="center" py={2}>
          <Typography variant="body2" mb={1}>
            Failed to fetch memory books
          </Typography>
          <Button variant="contained" size="small" onClick={fetchMemoryBooks}>
            Retry
          </Button>
        </Box>
      )}

      {!error && !isLoading && uploadedBooks.length === 0 && (
        <Typography variant="body2" textAlign="center" py={2}>
          No memory books available at the moment
        </Typography>
      )}

      {!error &&
        !isLoading &&
        uploadedBooks.map((memoryBook) => (
          <MemoryBookTableRow
            key={memoryBook.id}
            memoryBook={memoryBook}
            onDelete={deleteMemoryBook}
            isMobile
          />
        ))}
    </Box>
  );

  const historyTable = (
    <TableContainer component={Paper}>
      <Table sx={{ minWidth: 500 }} aria-label="upload history">
        <TableHead sx={{ backgroundColor: colors.primary[800] }}>
          <TableRow>
            <TableCell sx={{ fontWeight: 600 }}>File Name</TableCell>
            <TableCell sx={{ fontWeight: 600 }}>Uploaded by</TableCell>
            <TableCell sx={{ fontWeight: 600 }}>Date</TableCell>
            <TableCell sx={{ fontWeight: 600 }}>Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {isLoading && (
            <TableRow>
              <TableCell align="center" colSpan={4}>
                <CircularProgress size={20} />
              </TableCell>
            </TableRow>
          )}
          {error && !isLoading && (
            <TableRow>
              <TableCell align="center" colSpan={4}>
                <Box py={1}>
                  <Typography variant="body2" align="center">
                    Failed to fetch memory books
                  </Typography>
                  <Box display="flex" justifyContent="center" mt={1}>
                    <Button
                      onClick={fetchMemoryBooks}
                      variant="contained"
                      size="small"
                    >
                      Retry
                    </Button>
                  </Box>
                </Box>
              </TableCell>
            </TableRow>
          )}
          {!error && !isLoading && uploadedBooks.length === 0 && (
            <TableRow>
              <TableCell colSpan={4} align="center">
                No memory books available at the moment
              </TableCell>
            </TableRow>
          )}
          {!error &&
            !isLoading &&
            uploadedBooks.map((memoryBook) => (
              <MemoryBookTableRow
                key={memoryBook.id}
                memoryBook={memoryBook}
                onDelete={deleteMemoryBook}
              />
            ))}
        </TableBody>
      </Table>
    </TableContainer>
  );

  return (
    <Box bgcolor={colors.primary[900]} p={2} borderRadius={2}>
      <Typography variant="h5" fontWeight="bold" mb={3}>
        Book and Upload
      </Typography>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
          gap: 2,
          mb: 3,
        }}
      >
        {dropZone}
        {notesPanel}
      </Box>

      <Divider sx={{ mb: 2 }} />

      <CurrentMemoryBookInfo
        memoryBooks={memoryBooks}
        isLoading={isLoading}
        error={error}
        uploadUser={uploadUser}
        isAdmin={true}
      />

      <Typography variant="body1" fontWeight={500} mb={1.5}>
        Upload History
      </Typography>

      {isMobile ? historyMobile : historyTable}
    </Box>
  );
};
